import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { authClient } from '../utils/amplifyClient';
import * as Sentry from '@sentry/browser';

import { useUserContext } from '../contexts/UserContext';

import { deleteForm as deleteFormMutation } from '../graphql/mutations';
import {
    listFormTemplateGroups as listFormTemplateGroupsQuery,
    listFormTemplates as listFormTemplatesQuery,
} from '../graphql/queries';

import { FormTemplate, Form as FormType } from '../types/schema';

import '../css/FormsContent.css';
import { useFormsContext } from '../contexts/FormsContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Btn, BtnIconOnly, Card, DataTable, DataTableNoRowDrag, DataTableSortableHandle, IconPencil, IconTrash, PageHeader, TabNav } from './ui';
import type { DataTableColumn, DataTableSortableAccessibility, SortableRowAriaOptions } from './ui';
import { FormsDeleteModal } from './pages-forms/FormsDeleteModal';
import { FormsTemplateDropdown } from './pages-forms/FormsTemplateDropdown';
import { DragHandleIcon } from './pages-forms/FormBuilderShared';
import {
    hasStoredHomeFavoriteFormIds,
    reorderHomeFavoriteFormIds,
    resolveHomeFavoriteFormIds,
    toggleHomeFavoriteFormId,
} from '../utils/homeFormFavorites';
import { FORM_TYPES, formTypeFromQueryParam, formTypeToQueryParam } from '../utils/constants';

const FORMS_FAVORITE_SORT_INSTRUCTIONS_ID = 'forms-home-favorites-sort-instructions';

function FormsFavoriteTableCell({
    form,
    isFavorite,
    onToggleFavorite,
}: {
    form: FormType;
    isFavorite: boolean;
    onToggleFavorite: (formId: string) => void;
}) {
    const formId = form.id as string | undefined;

    return (
        <div className="table-actions forms-favorite-actions">
            {isFavorite ? (
                <DataTableSortableHandle
                    className="btn btn-secondary-outlined btn-sm forms-favorite-drag-handle"
                    ariaLabel={`Reorder ${form.name ?? 'form'} in home favorites`}
                >
                    <span className="btn-icon-wrap" aria-hidden="true">
                        <DragHandleIcon />
                    </span>
                </DataTableSortableHandle>
            ) : (
                <span className="forms-favorite-drag-spacer" aria-hidden="true" />
            )}
            <DataTableNoRowDrag>
                <Btn
                variant="tertiary"
                buttonSize="sm"
                buttonClasses={`btn-icon forms-favorite-toggle${isFavorite ? ' is-favorite' : ''}`}
                buttonText={isFavorite ? '★' : '☆'}
                ariaLabel={
                    isFavorite
                        ? `Remove ${form.name ?? 'form'} from home favorites`
                        : `Add ${form.name ?? 'form'} to home favorites`
                }
                buttonOnClick={() => {
                    if (formId) {
                        onToggleFavorite(formId);
                    }
                }}
            />
            </DataTableNoRowDrag>
        </div>
    );
}

// TODO: !!! Handle creation of PM form and make sure that I'm setting the type of form properly when fetching and saving, look at query and filtering! Add PM forms to it all

export const FormsContent = () => {
    const [isLoading] = useState(false);

    const [isDeleting, setIsDeleting] = useState(false);

    const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
    const [selectedForm, setSelectedForm] = useState<FormType | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const formTypeFromUrl =
        formTypeFromQueryParam(searchParams.get('type')) === FORM_TYPES.DAILY_SERVICE
            ? 'dailyServiceForms'
            : 'inspectionForms';
    const [formType, setFormType] = useState(formTypeFromUrl);
    const [inspectionFormsNumber, setInspectionFormsNumber] = useState<number>(0);
    const [dailyServicFormsNumber, setDailyServiceFormsNumber] = useState<number>(0);
    const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
    const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
    const [nameFilter, setNameFilter] = useState('');
    const [filterStackFullWidth, setFilterStackFullWidth] = useState(false);
    const [favoriteFormIds, setFavoriteFormIds] = useState<string[]>([]);
    const navigate = useNavigate();
    const { user: authUser } = useAuthenticator((context) => [context.user]);

    const { company, companyOwner, companyUser } = useUserContext();
    const { companyInspectionForms, companyDailyServiceForms, refreshForms, loading } = useFormsContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;
    const favoriteScopeUserId =
        companyUser?.id || companyOwner?.id || authUser?.userId || null;

    const defaultFavoriteFormIds = useMemo(() => {
        const startId = companyInspectionForms.find((form) =>
            form.name?.toLowerCase().startsWith('start of day'),
        )?.id;
        const endId = companyInspectionForms.find((form) => form.name?.toLowerCase().startsWith('end of day'))?.id;
        return [startId, endId].filter((id): id is string => Boolean(id));
    }, [companyInspectionForms]);

    useEffect(() => {
        if (!companyId || !favoriteScopeUserId) {
            return;
        }

        setFavoriteFormIds((current) => {
            if (hasStoredHomeFavoriteFormIds(companyId, favoriteScopeUserId)) {
                return resolveHomeFavoriteFormIds(companyId, favoriteScopeUserId, defaultFavoriteFormIds);
            }

            if (current.length > 0) {
                return current;
            }

            return resolveHomeFavoriteFormIds(companyId, favoriteScopeUserId, defaultFavoriteFormIds);
        });
    }, [companyId, defaultFavoriteFormIds, favoriteScopeUserId]);

    const handleToggleFavorite = (formId: string) => {
        setFavoriteFormIds(toggleHomeFavoriteFormId(formId, companyId, favoriteScopeUserId, defaultFavoriteFormIds));
    };

    useEffect(() => {
        setFormType(formTypeFromUrl);
    }, [formTypeFromUrl]);

    const handleFormTypeTabChange = (tabId: string) => {
        setFormType(tabId);
        const nextType = tabId === 'dailyServiceForms' ? FORM_TYPES.DAILY_SERVICE : FORM_TYPES.INSPECTION;
        setSearchParams({ type: formTypeToQueryParam(nextType) }, { replace: true });
    };

    const handleFavoriteDragReorder = (activeId: string, overId: string) => {
        setFavoriteFormIds(
            reorderHomeFavoriteFormIds(
                activeId,
                overId,
                companyId,
                favoriteScopeUserId,
                defaultFavoriteFormIds,
            ),
        );
    };

    const allFormsForType = useMemo(() => {
        if (formType === 'inspectionForms') {
            return companyInspectionForms;
        }
        if (formType === 'dailyServiceForms') {
            return companyDailyServiceForms;
        }

        return [];
    }, [companyDailyServiceForms, companyInspectionForms, formType]);

    const formsForType = useMemo(() => {
        let forms: FormType[] = [];
        if (formType === 'inspectionForms') {
            forms = companyInspectionForms;
        } else if (formType === 'dailyServiceForms') {
            forms = companyDailyServiceForms;
        }

        if (statusFilter === 'published') {
            return forms.filter((form) => form.status !== 'draft');
        }

        if (statusFilter === 'draft') {
            return forms.filter((form) => form.status === 'draft');
        }

        return forms;
    }, [companyDailyServiceForms, companyInspectionForms, formType, statusFilter]);

    const isStatusSortable = useMemo(() => {
        const statusTypes = new Set(formsForType.map((form) => (form.status === 'draft' ? 'draft' : 'published')));
        return statusTypes.size > 1;
    }, [formsForType]);

    const filteredForms = useMemo(() => {
        const query = nameFilter.trim().toLowerCase();
        const forms = query
            ? formsForType.filter((form) => form.name?.toLowerCase().includes(query))
            : formsForType;

        const favoriteIndex = new Map(favoriteFormIds.map((id, index) => [id, index]));
        const favorites: FormType[] = [];
        const unfavorited: FormType[] = [];

        for (const form of forms) {
            if (form.id && favoriteIndex.has(form.id)) {
                favorites.push(form);
            } else {
                unfavorited.push(form);
            }
        }

        favorites.sort(
            (a, b) => (favoriteIndex.get(a.id as string) ?? 0) - (favoriteIndex.get(b.id as string) ?? 0),
        );
        unfavorited.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));

        return [...favorites, ...unfavorited];
    }, [favoriteFormIds, formsForType, nameFilter]);

    const sortableFavoriteRowIds = useMemo(
        () =>
            filteredForms
                .filter((form) => form.id && favoriteFormIds.includes(form.id))
                .map((form) => form.id as string),
        [favoriteFormIds, filteredForms],
    );

    const formNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const form of allFormsForType) {
            if (form.id) {
                map.set(form.id, form.name ?? 'Untitled form');
            }
        }
        return map;
    }, [allFormsForType]);

    const sortableAccessibility = useMemo((): DataTableSortableAccessibility => {
        const formName = (id: string | number) => formNameById.get(String(id)) ?? 'Form';

        return {
            announcements: {
                onDragStart({ active }) {
                    return `Picked up ${formName(active.id)} for reordering. Use the arrow keys to move, then Space to drop, or Escape to cancel.`;
                },
                onDragOver({ active, over }) {
                    if (!over || active.id === over.id) {
                        return undefined;
                    }
                    return `${formName(active.id)} moved in the home favorites list.`;
                },
                onDragEnd({ active, over }) {
                    if (!over || active.id === over.id) {
                        return undefined;
                    }
                    return `${formName(active.id)} dropped. Home favorite order updated.`;
                },
                onDragCancel({ active }) {
                    return `Reorder cancelled. ${formName(active.id)} was not moved.`;
                },
            },
        };
    }, [formNameById]);

    const getSortableRowAriaLabel = useCallback(
        ({ row, sortableIndex, sortableCount }: SortableRowAriaOptions<FormType>) =>
            `${row.name ?? 'Untitled form'}, home favorite, position ${sortableIndex + 1} of ${sortableCount}. Use the drag handle to reorder.`,
        [],
    );

    const formTableColumns = useMemo<DataTableColumn<FormType>[]>(
        () => [
            {
                id: 'favorite',
                header: 'Favorite',
                headerLabel: 'Favorite and home display order',
                align: 'center',
                thClassName: 'data-table-col-narrow forms-table-col-favorite',
                tdClassName: 'data-table-col-narrow forms-table-col-favorite',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (form) => {
                    const formId = form.id as string | undefined;
                    const isFavorite = Boolean(formId && favoriteFormIds.includes(formId));

                    return (
                        <FormsFavoriteTableCell
                            form={form}
                            isFavorite={isFavorite}
                            onToggleFavorite={handleToggleFavorite}
                        />
                    );
                },
            },
            {
                id: 'name',
                header: 'Name',
                sortable: true,
                sortType: 'alpha',
                sortValue: (form) => form.name ?? '',
                filterClassName: 'forms-table-filter-name',
                filterRowClassName: 'data-table-th-filter-field',
                mobileCardPrimary: true,
                renderFilter: () => (
                    <input
                        type="search"
                        value={nameFilter}
                        placeholder="Type to filter by Form name"
                        aria-label="Type to filter by Form name"
                        onChange={(event) => setNameFilter(event.target.value)}
                    />
                ),
                renderCell: (form) => form.name,
            },
            {
                id: 'status',
                header: 'Status',
                align: 'center',
                sortable: isStatusSortable,
                sortType: 'alpha',
                sortValue: (form) => (form.status === 'draft' ? 'Draft' : 'Published'),
                thClassName: 'forms-table-col-status data-table-col-narrow',
                tdClassName: 'forms-table-col-status data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                filterColSpan: 3,
                filterClassName: 'forms-table-filter-status',
                filterRowClassName: 'data-table-th-filter-actions',
                filterLabel: 'Filter by status',
                renderFilter: () => (
                    <div className="forms-status-filter" role="group" aria-label="Filter by status">
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="All"
                            buttonClasses={statusFilter === 'all' ? 'active' : undefined}
                            buttonOnClick={() => setStatusFilter('all')}
                            buttonAriaProps={{ 'aria-pressed': statusFilter === 'all' }}
                        />
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Published"
                            buttonClasses={statusFilter === 'published' ? 'active' : undefined}
                            buttonOnClick={() => setStatusFilter('published')}
                            buttonAriaProps={{ 'aria-pressed': statusFilter === 'published' }}
                        />
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Drafts"
                            buttonClasses={statusFilter === 'draft' ? 'active' : undefined}
                            buttonOnClick={() => setStatusFilter('draft')}
                            buttonAriaProps={{ 'aria-pressed': statusFilter === 'draft' }}
                        />
                    </div>
                ),
                renderCell: (form) => (
                    <Badge
                        badgeClasses={form.status === 'draft' ? 'badge-warning' : 'badge-success'}
                        badgeText={form.status === 'draft' ? 'Draft' : 'Published'}
                    />
                ),
            },
            {
                id: 'edit',
                header: 'Edit',
                align: 'center',
                thClassName: 'data-table-col-narrow',
                tdClassName: 'data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (form) => (
                    <DataTableNoRowDrag className="table-actions">
                        <BtnIconOnly
                            size="sm"
                            icon={<IconPencil />}
                            ariaLabel={`Edit ${form.name ?? 'form'}`}
                            onClick={() => navigate(`/forms/edit/${form.id}`)}
                        />
                    </DataTableNoRowDrag>
                ),
            },
            {
                id: 'delete',
                header: 'Delete',
                align: 'center',
                thClassName: 'data-table-col-narrow',
                tdClassName: 'data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (form) => (
                    <DataTableNoRowDrag className="table-actions">
                        <BtnIconOnly
                            size="sm"
                            variant="danger"
                            icon={<IconTrash />}
                            ariaLabel={`Delete ${form.name ?? 'form'}`}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setSelectedForm(form);
                            }}
                        />
                    </DataTableNoRowDrag>
                ),
            },
        ],
        [favoriteFormIds, isStatusSortable, nameFilter, navigate, statusFilter],
    );

    useEffect(() => {
        if (!loading) {
            setInspectionFormsNumber(companyInspectionForms.length);
            setDailyServiceFormsNumber(companyDailyServiceForms.length);
        }
        getFormTemplates();
    }, [companyDailyServiceForms, companyInspectionForms, loading]);

    useEffect(() => {
        setNameFilter('');
    }, [formType, statusFilter]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 688px)');
        const syncFilterLayout = () => setFilterStackFullWidth(mediaQuery.matches);

        syncFilterLayout();
        mediaQuery.addEventListener('change', syncFilterLayout);

        return () => mediaQuery.removeEventListener('change', syncFilterLayout);
    }, []);

    const getFormTemplates = async () => {
        if (!companyId) return;

        try {
            const formTemplateGroupResponse = await authClient.graphql({
                query: listFormTemplateGroupsQuery,
            });
            const formTemplateGroups = formTemplateGroupResponse.data.listFormTemplateGroups.items;
            const filteredFormTemplateGroups = formTemplateGroups.filter(
                (templateGroup) => companyId && templateGroup.sharedWithCompanyIds?.includes(companyId),
            );

            const formTemplateGroupIds = filteredFormTemplateGroups.map((templateGroup) => templateGroup.id);

            if (formTemplateGroupIds.length === 0) {
                setFormTemplates([]);
                return;
            }

            const allFormTemplates: FormTemplate[] = [];
            let nextToken: string | null | undefined = undefined;
            do {
                const formTemplateResponse = await authClient.graphql({
                    query: listFormTemplatesQuery,
                    variables: nextToken ? { nextToken } : undefined,
                });
                const page: { items: unknown[]; nextToken?: string | null } =
                    formTemplateResponse.data.listFormTemplates;
                allFormTemplates.push(...(page.items as FormTemplate[]));
                nextToken = page.nextToken;
            } while (nextToken);

            const filteredFormTemplates = allFormTemplates.filter(
                (template) =>
                    template.formTemplateGroupID && formTemplateGroupIds.includes(template.formTemplateGroupID),
            );

            setFormTemplates(filteredFormTemplates);
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        }
    };

    if (loading) {
        return <div className="forms-loading">Loading forms...</div>;
    }

    const handleDeleteSelectedForm = async () => {
        if (!selectedForm?.id) {
            return;
        }
        setIsDeleting(true);
        try {
            await authClient.graphql({
                query: deleteFormMutation,
                variables: {
                    input: {
                        id: selectedForm.id,
                    },
                },
            });
            refreshForms();
            setSelectedForm(null);
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="page-container forms-content">
            {/* Header */}
            <PageHeader
                title="Forms"
                actions={
                    <>
                        <button
                            className="btn-create-form"
                            onClick={() => {
                                const nextFormType =
                                    formType === 'dailyServiceForms'
                                        ? FORM_TYPES.DAILY_SERVICE
                                        : FORM_TYPES.INSPECTION;
                                navigate(`/forms/new?type=${formTypeToQueryParam(nextFormType)}`);
                            }}
                        >
                            Create a New Form
                        </button>
                        <div className="forms-template-dropdown-anchor">
                            <button
                                className="btn-template"
                                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                            >
                                Start From Template ▼
                            </button>
                            {showTemplateDropdown && (
                                <FormsTemplateDropdown
                                    formTemplates={formTemplates}
                                    scope={
                                        formType === 'dailyServiceForms' ? 'dailyService' : 'inspection'
                                    }
                                    companyForms={allFormsForType}
                                    onSelectLibraryTemplate={(templateId) => {
                                        setShowTemplateDropdown(false);
                                        navigate(`/forms/template/${templateId}`);
                                    }}
                                    onSelectCompanyForm={(sourceFormId) => {
                                        setShowTemplateDropdown(false);
                                        const nextFormType =
                                            formType === 'dailyServiceForms'
                                                ? FORM_TYPES.DAILY_SERVICE
                                                : FORM_TYPES.INSPECTION;
                                        navigate(
                                            `/forms/new?type=${formTypeToQueryParam(nextFormType)}&fromFormId=${encodeURIComponent(sourceFormId)}`,
                                        );
                                    }}
                                />
                            )}
                        </div>
                    </>
                }
            />

            {/* Tabs */}
            <div className="tab-component">
                <TabNav
                    variant="tabs"
                    ariaLabel="Form types"
                    tabButtonClasses="forms-tab"
                    tabs={[
                        {
                            id: 'inspectionForms',
                            label: `Inspection Forms (${inspectionFormsNumber})`,
                        },
                        {
                            id: 'dailyServiceForms',
                            label: `Daily Service Forms (${dailyServicFormsNumber})`,
                        },
                    ]}
                    activeTabId={formType}
                    onTabChange={handleFormTypeTabChange}
                    flared={true}
                />

                {/* Table */}
                <Card className="tab-body forms-table-container" variant="card-lg">
                    {isLoading ? (
                        <div className="forms-loading">Loading Forms...</div>
                    ) : (
                        <>
                            <p id={FORMS_FAVORITE_SORT_INSTRUCTIONS_ID} className="data-table-visually-hidden">
                                Favorited forms appear at the top of the table and define the order shown on the home
                                page. Focus the drag handle on a favorited row, then press Space or Enter to pick it up. Use the Arrow Up
                                and Arrow Down keys to move it, then press Space or Enter to drop. Press Escape to
                                cancel. On touch devices, drag only the handle so the page can still scroll.
                            </p>
                            <DataTable
                            caption="Forms list"
                            captionVisuallyHidden
                            columns={formTableColumns}
                            rows={filteredForms}
                            getRowKey={(form) => form.id}
                            sortableRowIds={sortableFavoriteRowIds}
                            onSortableRowDragEnd={handleFavoriteDragReorder}
                            sortableInstructionsId={FORMS_FAVORITE_SORT_INSTRUCTIONS_ID}
                            getSortableRowAriaLabel={getSortableRowAriaLabel}
                            sortableAccessibility={sortableAccessibility}
                            filterable
                            filterStackFullWidth={filterStackFullWidth}
                            hideHeaderWhenEmpty
                            emptyState={
                                allFormsForType.length === 0 ? (
                                    <div className="forms-empty">
                                        <h3>No forms</h3>
                                        <p>Create your first form to get started</p>
                                    </div>
                                ) : (
                                    'No forms match your filters.'
                                )
                            }
                        />
                        </>
                    )}
                </Card>
            </div>

            {/* Modal For Deleting a Form */}
            <FormsDeleteModal
                selectedForm={selectedForm}
                isDeleting={isDeleting}
                onDismiss={() => setSelectedForm(null)}
                onConfirmDelete={handleDeleteSelectedForm}
            />
        </div>
    );
};
